import { GUEST_FIELDS, type GuestField, type JoinSharedDeskRequest, type ShareAccess, type SharedDeskInfo } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { Eye, MessagesSquare } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Field } from '@/components/field';
import { LanguageSwitch } from '@/components/language-switch';
import { Logo } from '@/components/logo';
import { ErrorState, LoadingPage } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { api, ApiError, errorMessage, getToken, setToken } from '@/lib/api';
import { useBrandLook } from '@/lib/brand';
import { guestToken, setGuestToken } from '@/lib/guest';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

const FIELD_LABEL: Record<GuestField, string> = {
  firstName: t('First name'),
  lastName: t('Last name'),
  company: t('Company'),
  email: t('Email'),
  phone: t('Phone'),
};

export const ACCESS_TEXT: Record<Exclude<ShareAccess, 'none'>, { label: string; description: string }> = {
  read: { label: t('See it'), description: t('See the desk’s views and results.') },
  write: { label: t('Work with it'), description: t('See its views and chat with its Lead, who takes on work for you.') },
};

/**
 * `/s/<token>`: a desk shared by link (docs/features/desk-sharing.md). Signed in, the person goes on as themselves;
 * otherwise they fill in what the account asks, without an account or registering through the link.
 */
export function SharedDeskPage() {
  const { token } = useParams({ from: '/s/$token' });
  const navigate = useNavigate();
  const signedIn = !!getToken();
  const info = useQuery({ queryKey: ['shared', token, signedIn], queryFn: () => api.sharing.info(token, signedIn), retry: false });
  useBrandLook(info.data?.branding);

  // Came here before on this device by the form: straight in.
  const deskId = info.data?.deskId;
  useEffect(() => {
    if (deskId && !signedIn && guestToken(deskId)) void navigate({ to: '/guest/$deskId', params: { deskId }, replace: true });
  }, [deskId, signedIn, navigate]);

  if (info.isPending) return <LoadingPage />;
  if (info.isError) {
    return (
      <Frame>
        <ErrorState error={info.error} />
      </Frame>
    );
  }
  return (
    <Frame info={info.data}>
      {signedIn ? <SignedInJoin token={token} info={info.data} /> : <FormJoin token={token} info={info.data} />}
    </Frame>
  );
}

function Frame({ info, children }: { info?: SharedDeskInfo; children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-full flex-col bg-background sm:items-center sm:justify-center sm:bg-muted/40 sm:px-4 sm:py-12">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[42vh] bg-[radial-gradient(120%_90%_at_50%_0%,color-mix(in_oklch,var(--primary)_28%,transparent),transparent_70%)] sm:h-full sm:bg-[radial-gradient(60%_50%_at_50%_0%,color-mix(in_oklch,var(--primary)_14%,transparent),transparent_70%)]"
      />
      <div className="absolute top-[max(0.75rem,env(safe-area-inset-top))] right-3 z-10">
        <LanguageSwitch />
      </div>
      <div className="relative mx-auto flex w-full max-w-md flex-1 flex-col px-6 pt-[max(4rem,calc(env(safe-area-inset-top)+3rem))] pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:flex-none sm:px-0 sm:pt-0 sm:pb-0">
        {info && (
          <div className="mb-6 flex flex-col items-center gap-3 text-center">
            <Logo src={info.branding?.logoUrl} className="size-14 drop-shadow-sm sm:size-12" />
            <div>
              <p className="text-sm text-muted-foreground">{t('{account} shares a desk with you', { account: info.accountName })}</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight">{info.deskName}</h1>
              {info.deskDescription && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{info.deskDescription}</p>}
            </div>
          </div>
        )}
        {children}
        <p className="mt-auto pt-8 text-center text-xs text-muted-foreground sm:mt-6 sm:pt-0">
          {t('Runs on')}{' '}
          <a href="https://deskworks.ai" className="font-medium hover:text-foreground">
            Deskworks
          </a>
        </p>
      </div>
    </div>
  );
}

/** Signed in: one click, as themselves; a member of the desk's workspace lands on their own page of it. */
function SignedInJoin({ token, info }: { token: string; info: SharedDeskInfo }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  async function go(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.sharing.join(token, { password: password || undefined }, true);
      void queryClient.invalidateQueries({ queryKey: qk.me });
      router.history.push(res.memberPath ?? `/guest/${res.deskId}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // Already in, or nothing to ask: straight on.
  useEffect(() => {
    if (started.current || (!info.joined && info.passwordRequired)) return;
    if (info.joined || info.registeredAccess !== 'none') {
      started.current = true;
      void go();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  if (info.registeredAccess === 'none' && !info.joined) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t('This link is open only without a Deskworks account. Sign out to open it with your details, or ask {account} for access.', { account: info.accountName })}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={go} className="grid gap-4">
          {info.passwordRequired && !info.joined && (
            <Field label={t('Password for this link')}>
              <Input type="password" autoFocus required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          )}
          {error && (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" loading={busy}>
            {t('Open the desk')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

type Way = 'anyone' | 'register';

/** Not signed in: the form the account set up, without an account or registering one; signing in is a link away. */
function FormJoin({ token, info }: { token: string; info: SharedDeskInfo }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ways: Way[] = [...(info.anyoneAccess !== 'none' ? (['anyone'] as const) : []), ...(info.registeredAccess !== 'none' ? (['register'] as const) : [])];
  const [way, setWay] = useState<Way>(ways[0] ?? 'register');
  const [form, setForm] = useState<Record<GuestField, string>>({ firstName: '', lastName: '', company: '', email: '', phone: '' });
  const [linkPassword, setLinkPassword] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<{ message: string; signIn?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const signInHref = `/login?redirect=${encodeURIComponent(`/s/${token}`)}`;

  if (!ways.length) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">{t('This link is not open right now.')}</CardContent>
      </Card>
    );
  }

  const registering = way === 'register';
  const mode = (field: GuestField) => (field === 'email' && registering ? 'required' : info.fields[field]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (registering && accountPassword.length < 12) {
      setError({ message: t('The password needs at least 12 characters.') });
      return;
    }
    setBusy(true);
    try {
      const body: JoinSharedDeskRequest = {
        ...Object.fromEntries(GUEST_FIELDS.filter((f) => mode(f) !== 'off' && form[f].trim()).map((f) => [f, form[f].trim()])),
        consent,
        ...(info.passwordRequired ? { password: linkPassword } : {}),
        ...(registering ? { register: { password: accountPassword } } : {}),
      };
      const res = await api.sharing.join(token, body, false);
      if (res.session) {
        queryClient.clear();
        setToken(res.session.token);
      }
      if (res.guestToken) setGuestToken(res.deskId, res.guestToken);
      void navigate({ to: '/guest/$deskId', params: { deskId: res.deskId } });
    } catch (err) {
      setError({ message: errorMessage(err), signIn: err instanceof ApiError && err.status === 409 });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border-0 bg-transparent shadow-none sm:border sm:bg-card sm:shadow-sm">
      <CardContent className="px-0 pt-0 sm:px-6 sm:pt-6">
        {ways.length > 1 && (
          <div role="radiogroup" aria-label={t('How you come in')} className="mb-4 grid grid-cols-2 gap-2">
            {ways.map((w) => {
              const access = w === 'anyone' ? info.anyoneAccess : info.registeredAccess;
              return (
                <button
                  key={w}
                  type="button"
                  role="radio"
                  aria-checked={way === w}
                  onClick={() => setWay(w)}
                  className={cn(
                    'rounded-lg border p-3 text-left text-sm transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none',
                    way === w && 'border-primary bg-primary/5 hover:bg-primary/5',
                  )}
                >
                  <span className="font-medium">{w === 'anyone' ? t('Without an account') : t('With an account')}</span>
                  <AccessLine access={access} />
                </button>
              );
            })}
          </div>
        )}
        {ways.length === 1 && (
          <p className="mb-4 rounded-lg bg-muted/60 px-3 py-2 text-sm">
            <AccessLine access={registering ? info.registeredAccess : info.anyoneAccess} />
          </p>
        )}
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {GUEST_FIELDS.filter((f) => mode(f) !== 'off').map((f) => (
              <Field key={f} label={FIELD_LABEL[f]} optional={mode(f) === 'optional'} className={f === 'firstName' || f === 'lastName' ? undefined : 'sm:col-span-2'}>
                <Input
                  type={f === 'email' ? 'email' : f === 'phone' ? 'tel' : 'text'}
                  autoComplete={{ firstName: 'given-name', lastName: 'family-name', company: 'organization', email: 'email', phone: 'tel' }[f]}
                  required={mode(f) === 'required'}
                  maxLength={f === 'company' ? 120 : 200}
                  value={form[f]}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                />
              </Field>
            ))}
          </div>
          {registering && (
            <Field label={t('Password for your account')} hint={t('At least 12 characters. You sign in with your email and this password.')}>
              <Input type="password" autoComplete="new-password" required minLength={12} value={accountPassword} onChange={(e) => setAccountPassword(e.target.value)} />
            </Field>
          )}
          {info.passwordRequired && (
            <Field label={t('Password for this link')} hint={t('{account} gave it to you with the link.', { account: info.accountName })}>
              <Input type="password" required value={linkPassword} onChange={(e) => setLinkPassword(e.target.value)} />
            </Field>
          )}
          <label className="flex cursor-pointer items-start gap-2.5 py-1 text-sm select-none">
            <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} required className="mt-0.5" />
            <span>{t('I agree that {account} stores my details and may contact me about this.', { account: info.accountName })}</span>
          </label>
          {error && (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}{' '}
              {error.signIn && (
                <a href={signInHref} className="font-medium underline">
                  {t('Sign in')}
                </a>
              )}
            </p>
          )}
          <Button type="submit" loading={busy} className="h-11 sm:h-9">
            {registering ? t('Create account and open') : t('Open the desk')}
          </Button>
        </form>
        {info.registeredAccess !== 'none' && (
          <p className="mt-4 text-center text-sm text-muted-foreground">
            {t('Already on Deskworks?')}{' '}
            <Link to="/login" search={{ redirect: `/s/${token}` }} className="font-medium text-primary hover:underline">
              {t('Sign in')}
            </Link>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AccessLine({ access }: { access: ShareAccess }) {
  if (access === 'none') return null;
  const Icon = access === 'write' ? MessagesSquare : Eye;
  return (
    <span className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
      <Icon className="mt-px size-3.5 shrink-0" />
      <span>
        <span className="font-medium text-foreground/80">{ACCESS_TEXT[access].label}:</span> {ACCESS_TEXT[access].description}
      </span>
    </span>
  );
}
